// VEPC Gestão — Tela Oficial de Onboarding e Primeiro Acesso do Cliente
// Autenticação Real com Firebase Authentication SDK & Ativação Segura
import { api } from '../api.js';
import { escapeHtml } from '../utils.js';
import {
  auth,
  isLocalHost,
  isEmulatorConnected,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  obterLinkVerificacaoEmulador
} from '../firebase.js';

export async function renderOnboarding() {
  const container = document.createElement('div');
  container.className = 'onboarding-wrapper';

  const params = new URLSearchParams(window.location.search);
  const rawToken = (params.get('token') || '').trim();

  container.innerHTML = `
    <div class="onboarding-card">
      <div class="onboarding-header">
        <div class="onboarding-logo-badge">V</div>
        <h1 class="onboarding-title">VEPC Gestão</h1>
        <p class="onboarding-subtitle">Primeiro Acesso & Ativação Segura de Conta</p>
        <div class="onboarding-beta-tag">⭐ 7 DIAS DE AVALIAÇÃO GRATUITA</div>
      </div>

      <div id="onboarding-content">
        <div class="onboarding-loading">
          <div class="spinner"></div>
          <p>Verificando autenticidade e validade do convite...</p>
        </div>
      </div>

      <div class="onboarding-footer">
        <span>VEPC Soluções Digitais &bull; Autenticação Criptográfica Firebase Auth</span>
      </div>
    </div>
  `;

  // Se não forneceu token no link
  if (!rawToken || rawToken.length < 16) {
    setTimeout(() => {
      renderErroConvite(container, {
        titulo: 'Link de Acesso Incompleto ou Inválido',
        mensagem: 'O token individual de uso único não foi encontrado ou está incompleto no link fornecido.',
        codigo: 'ERR_TOKEN_CONVITE_INVALIDO',
        ajuda: 'Por favor, abra novamente o link exatamente como enviado no convite ou solicite um novo link ao administrador da VEPC.'
      });
    }, 100);
    return container;
  }

  // Validação assíncrona do convite com o backend
  try {
    const dadosConvite = await api.validarConvite(rawToken);
    renderFormularioCriacaoSenha(container, dadosConvite, rawToken);
  } catch (err) {
    renderErroConvite(container, {
      titulo: 'Não foi possível validar este convite',
      mensagem: err.message || 'Erro ao consultar o convite de primeiro acesso.',
      codigo: err.codigo || 'ERR_CONVITE_INVALIDO',
      ajuda: 'Se este convite já foi utilizado ou expirou, entre em contato com o suporte da VEPC para emitir um novo acesso.'
    });
  }

  return container;
}

function renderErroConvite(container, info) {
  const content = container.querySelector('#onboarding-content');
  content.innerHTML = `
    <div class="onboarding-error-box">
      <div class="error-icon">⚠️</div>
      <h3 class="error-title">${escapeHtml(info.titulo)}</h3>
      <p class="error-message">${escapeHtml(info.mensagem)}</p>
      ${info.codigo ? `<div class="error-code">Código do Erro: <code>${escapeHtml(info.codigo)}</code></div>` : ''}
      <p class="error-help">${escapeHtml(info.ajuda)}</p>
      <div style="margin-top: 20px; display: flex; gap: 10px; justify-content: center;">
        <a href="/" class="btn btn-secondary">Voltar ao Início</a>
      </div>
    </div>
  `;
}

function renderFormularioCriacaoSenha(container, convite, token) {
  const content = container.querySelector('#onboarding-content');

  content.innerHTML = `
    <div class="onboarding-info-panel">
      <div class="info-row">
        <span class="info-label">Empresa:</span>
        <span class="info-value highlight">${escapeHtml(convite.empresa.nome)}</span>
      </div>
      ${convite.empresa.razao_social ? `
        <div class="info-row">
          <span class="info-label">Razão Social:</span>
          <span class="info-value">${escapeHtml(convite.empresa.razao_social)}</span>
        </div>
      ` : ''}
      <div class="info-row">
        <span class="info-label">Responsável Titular:</span>
        <span class="info-value">${escapeHtml(convite.usuario.nome)}</span>
      </div>
      <div class="info-row">
        <span class="info-label">E-mail Autorizado:</span>
        <span class="info-value code-email">${escapeHtml(convite.usuario.email)}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Plano Contratado:</span>
        <span class="info-value badge-plano">${escapeHtml(convite.plano.nome)}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Avaliação Gratuita:</span>
        <span class="info-value text-gold font-bold">7 Dias Grátis (Inicia exclusivamente após a ativação)</span>
      </div>
    </div>

    <form id="form-primeiro-acesso" class="onboarding-form">
      <div class="form-group">
        <label for="input-senha">Crie sua Senha Definitiva:</label>
        <div class="password-wrapper">
          <input 
            type="password" 
            id="input-senha" 
            class="form-control" 
            placeholder="Mínimo de 8 caracteres" 
            minlength="8" 
            required 
            autocomplete="new-password"
          />
          <button type="button" class="btn-toggle-pwd" id="btn-toggle-pwd" title="Mostrar/Ocultar Senha">👁️</button>
        </div>
        <div class="password-strength-meter" id="pwd-strength">
          <div class="meter-bar" id="meter-bar"></div>
        </div>
        <span class="pwd-hint">A senha será gravada exclusivamente no Firebase Authentication (não trafega na API administrativa).</span>
      </div>

      <div class="form-group">
        <label for="input-confirmar-senha">Confirme sua Senha:</label>
        <input 
          type="password" 
          id="input-confirmar-senha" 
          class="form-control" 
          placeholder="Digite a mesma senha novamente" 
          minlength="8" 
          required 
          autocomplete="new-password"
        />
      </div>

      <div class="form-checkbox">
        <label>
          <input type="checkbox" id="check-termos" required checked />
          Confirmo que sou o titular responsável pela empresa <strong>${escapeHtml(convite.empresa.nome)}</strong> e autorizo a ativação da conta.
        </label>
      </div>

      <div id="form-error-msg" class="form-error-banner" style="display: none;"></div>

      <button type="submit" id="btn-criar-senha" class="btn btn-gold btn-block btn-lg">
        Criar Credencial no Firebase & Prosseguir
      </button>
    </form>
  `;

  // Toggle visualização de senha
  const inputSenha = content.querySelector('#input-senha');
  const inputConf = content.querySelector('#input-confirmar-senha');
  const btnToggle = content.querySelector('#btn-toggle-pwd');
  btnToggle.onclick = () => {
    const isPass = inputSenha.type === 'password';
    inputSenha.type = isPass ? 'text' : 'password';
    inputConf.type = isPass ? 'text' : 'password';
    btnToggle.textContent = isPass ? '🔒' : '👁️';
  };

  // Medidor de força da senha
  const meterBar = content.querySelector('#meter-bar');
  inputSenha.oninput = () => {
    const val = inputSenha.value;
    let score = 0;
    if (val.length >= 8) score += 25;
    if (val.length >= 12) score += 25;
    if (/[A-Z]/.test(val) && /[a-z]/.test(val)) score += 25;
    if (/[0-9]/.test(val) && /[^A-Za-z0-9]/.test(val)) score += 25;

    meterBar.style.width = score + '%';
    if (score < 50) {
      meterBar.style.backgroundColor = '#EF4444';
    } else if (score < 75) {
      meterBar.style.backgroundColor = '#F59E0B';
    } else {
      meterBar.style.backgroundColor = '#10B981';
    }
  };

  // Submissão do Formulário
  const form = content.querySelector('#form-primeiro-acesso');
  const errorBanner = content.querySelector('#form-error-msg');
  const btnSubmit = content.querySelector('#btn-criar-senha');

  form.onsubmit = async (e) => {
    e.preventDefault();
    errorBanner.style.display = 'none';

    // Bloqueio estrito se estiver em localhost sem emulador conectado
    if (isLocalHost && !isEmulatorConnected) {
      errorBanner.innerHTML = `
        <strong>Ambiente Local Seguro:</strong> O Firebase Authentication Emulator não está conectado na porta 127.0.0.1:9099. O fallback para o Firebase de produção foi bloqueado por segurança para impedir criação acidental de dados em nuvem.
      `;
      errorBanner.style.display = 'block';
      return;
    }

    const senha = inputSenha.value;
    const confirmacao = inputConf.value;

    if (senha !== confirmacao) {
      errorBanner.textContent = 'A confirmação de senha não confere com a senha digitada.';
      errorBanner.style.display = 'block';
      inputConf.focus();
      return;
    }

    if (senha.length < 8) {
      errorBanner.textContent = 'A senha deve possuir no mínimo 8 caracteres.';
      errorBanner.style.display = 'block';
      inputSenha.focus();
      return;
    }

    btnSubmit.disabled = true;
    btnSubmit.innerHTML = '<div class="spinner-sm"></div> Autenticando com Firebase Auth...';

    try {
      // 1. Criação real da credencial no Firebase Authentication SDK
      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(auth, convite.usuario.email, senha);
      } catch (fbErr) {
        if (fbErr.code === 'auth/email-already-in-use') {
          // Se o usuário já criou credencial anteriormente, autentica para verificar o status
          userCredential = await signInWithEmailAndPassword(auth, convite.usuario.email, senha);
        } else {
          throw fbErr;
        }
      }

      const user = userCredential.user;

      // 2. Verificação de e-mail obrigatória
      if (!user.emailVerified) {
        try {
          await sendEmailVerification(user);
        } catch (sendErr) {
          console.warn('[Firebase] Aviso ao enviar e-mail de verificação:', sendErr);
        }
        renderAguardandoVerificacaoEmail(container, user, convite, token);
        return;
      }

      // 3. Se o e-mail já estiver verificado, obtém o Firebase ID Token legítimo
      const idToken = await user.getIdToken(true);

      // 4. Ativação atômica no backend enviando o Firebase ID Token verdadeiro
      const respostaAtivacao = await api.ativarOnboarding(token, idToken);
      renderSucessoAtivacao(container, respostaAtivacao, convite);

    } catch (err) {
      btnSubmit.disabled = false;
      btnSubmit.textContent = 'Criar Credencial no Firebase & Prosseguir';

      let msg = err.message || 'Erro inesperado na autenticação Firebase.';
      if (err.code === 'auth/weak-password') {
        msg = 'O Firebase rejeitou a senha por considerá-la fraca. Utilize ao menos 8 caracteres com letras e números.';
      } else if (err.code === 'auth/network-request-failed') {
        msg = 'Falha de comunicação com o Firebase Authentication Emulator (127.0.0.1:9099). Certifique-se de que o emulador está ativo.';
      } else if (err.code === 'auth/invalid-email') {
        msg = `O e-mail '${convite.usuario.email}' possui formato rejeitado pelo Firebase Authentication.`;
      }

      errorBanner.innerHTML = `
        <strong>Erro no Firebase Authentication (${escapeHtml(err.code || err.codigo || 'FALHA')}):</strong> ${escapeHtml(msg)}
      `;
      errorBanner.style.display = 'block';
    }
  };
}

function renderAguardandoVerificacaoEmail(container, user, convite, token) {
  const content = container.querySelector('#onboarding-content');

  content.innerHTML = `
    <div class="onboarding-verification-card">
      <div class="verification-mail-icon">✉️</div>
      <h2 class="verification-title">Confirme seu E-mail</h2>
      <p class="verification-desc">
        Sua senha foi cadastrada no Firebase com sucesso. Enviamos um link de verificação para o endereço:
      </p>
      <div class="verification-email-box">
        <strong>${escapeHtml(convite.usuario.email)}</strong>
      </div>

      <div class="verification-alert">
        🛡️ <strong>Regra de Segurança Estrita:</strong> A conta da empresa <strong>${escapeHtml(convite.empresa.nome)}</strong> só poderá ser ativada e o período de 7 dias iniciado após a comprovação de <code>email_verified: true</code> no Firebase.
      </div>

      ${isLocalHost ? `
        <div style="background: rgba(16, 185, 129, 0.1); border: 1px dashed var(--vepc-success); border-radius: 6px; padding: 12px; margin-bottom: 16px; font-size: 12px; text-align: left;">
          <div style="font-weight: 700; color: #10B981; margin-bottom: 4px;">⚡ Firebase Authentication Emulator Ativo</div>
          <p style="color: var(--vepc-text-muted); margin: 0 0 8px;">
            Em ambiente local de homologação, o link de verificação foi capturado pelo emulador local sem disparo de e-mail real para a internet.
          </p>
          <button type="button" id="btn-confirmar-emulador" class="btn btn-secondary btn-sm" style="width: 100%;">
            ⚡ Confirmar E-mail no Emulador Local Automaticamente
          </button>
        </div>
      ` : ''}

      <div id="verif-error-msg" class="form-error-banner" style="display: none; margin-bottom: 16px;"></div>

      <div style="display: flex; flex-direction: column; gap: 10px; margin-top: 10px;">
        <button id="btn-checar-verificacao" class="btn btn-gold btn-block btn-lg">
          Já Confirmei meu E-mail &bull; Concluir Ativação
        </button>
        <button id="btn-reenviar-email" class="btn btn-secondary btn-block">
          Reenviar E-mail de Confirmação
        </button>
      </div>
    </div>
  `;

  const btnChecar = content.querySelector('#btn-checar-verificacao');
  const btnReenviar = content.querySelector('#btn-reenviar-email');
  const errorBanner = content.querySelector('#verif-error-msg');
  const btnAutoConfirmar = content.querySelector('#btn-confirmar-emulador');

  if (btnAutoConfirmar) {
    btnAutoConfirmar.onclick = async () => {
      btnAutoConfirmar.disabled = true;
      btnAutoConfirmar.textContent = 'Localizando link no emulador...';
      try {
        const link = await obterLinkVerificacaoEmulador(convite.usuario.email);
        if (link) {
          await fetch(link);
          await user.reload();
          btnAutoConfirmar.textContent = '✔ E-mail Confirmado no Emulador!';
          btnAutoConfirmar.style.borderColor = '#10B981';
          btnAutoConfirmar.style.color = '#10B981';
          // Dispara a checagem automaticamente
          setTimeout(() => btnChecar.click(), 500);
        } else {
          alert('Link não encontrado nos registros do emulador. Clique em Reenviar E-mail.');
          btnAutoConfirmar.disabled = false;
          btnAutoConfirmar.textContent = '⚡ Confirmar E-mail no Emulador Local Automaticamente';
        }
      } catch (err) {
        alert('Erro ao confirmar via emulador: ' + err.message);
        btnAutoConfirmar.disabled = false;
        btnAutoConfirmar.textContent = '⚡ Confirmar E-mail no Emulador Local Automaticamente';
      }
    };
  }

  btnReenviar.onclick = async () => {
    try {
      btnReenviar.disabled = true;
      btnReenviar.textContent = 'Enviando...';
      await sendEmailVerification(user);
      alert('Novo link de verificação registrado no Firebase Emulator!');
    } catch (err) {
      alert('Erro ao reenviar e-mail: ' + err.message);
    } finally {
      btnReenviar.disabled = false;
      btnReenviar.textContent = 'Reenviar E-mail de Confirmação';
    }
  };

  btnChecar.onclick = async () => {
    errorBanner.style.display = 'none';
    btnChecar.disabled = true;
    btnChecar.innerHTML = '<div class="spinner-sm"></div> Verificando com Firebase...';

    try {
      // Recarrega o estado do usuário no Firebase
      await user.reload();

      if (!user.emailVerified) {
        errorBanner.innerHTML = `
          <strong>E-mail ainda não confirmado:</strong> O Firebase Authentication ainda indica que o e-mail não foi verificado. Utilize a opção de confirmação do emulador ou clique no link antes de prosseguir.
        `;
        errorBanner.style.display = 'block';
        btnChecar.disabled = false;
        btnChecar.textContent = 'Já Confirmei meu E-mail • Concluir Ativação';
        return;
      }

      // E-mail verificado! Obter o Firebase ID Token legítimo
      const idToken = await user.getIdToken(true);

      // Chamada atômica ao backend
      const respostaAtivacao = await api.ativarOnboarding(token, idToken);
      renderSucessoAtivacao(container, respostaAtivacao, convite);

    } catch (err) {
      btnChecar.disabled = false;
      btnChecar.textContent = 'Já Confirmei meu E-mail • Concluir Ativação';
      errorBanner.innerHTML = `
        <strong>Erro na Ativação (${escapeHtml(err.codigo || 'FALHA')}):</strong> ${escapeHtml(err.message)}
      `;
      errorBanner.style.display = 'block';
    }
  };
}

function renderSucessoAtivacao(container, res, convite) {
  const content = container.querySelector('#onboarding-content');
  content.innerHTML = `
    <div class="onboarding-success-card">
      <div class="success-shield-icon">✔</div>
      <h2 class="success-title">Primeiro Acesso Concluído!</h2>
      <p class="success-subtitle">Sua identidade Firebase foi confirmada e a conta ativada com segurança.</p>

      <div class="success-details-box">
        <div class="detail-line">
          <span>Empresa:</span>
          <strong>${escapeHtml(res.empresa_nome || convite.empresa.nome)}</strong>
        </div>
        <div class="detail-line">
          <span>Usuário Ativado:</span>
          <span>${escapeHtml(convite.usuario.email)}</span>
        </div>
        <div class="detail-line">
          <span>Data de Ativação (Marco Zero):</span>
          <strong class="text-emerald">${escapeHtml(res.ativada_em)}</strong>
        </div>
        <div class="detail-line">
          <span>Período de Avaliação Gratuita:</span>
          <strong class="text-gold">Válido até ${escapeHtml(res.avaliacao_ate || res.teste_expira_em)} (7 Dias)</strong>
        </div>
        <div class="detail-line">
          <span>Situação Atual da Conta:</span>
          <span class="badge badge-avaliacao">ATIVA &bull; TESTE 7 DIAS</span>
        </div>
      </div>

      <div class="success-next-steps">
        <h4>Próximos Passos:</h4>
        <ol>
          <li>Abra o aplicativo <strong>VEPC Gestão</strong> no seu celular Android.</li>
          <li>Faça login com seu e-mail (<strong>${escapeHtml(convite.usuario.email)}</strong>) e a senha criada no Firebase.</li>
          <li>Sua agenda, faturamento e veículos já estão configurados e prontos para uso.</li>
        </ol>
      </div>

      <div style="margin-top: 25px; display: flex; gap: 12px; justify-content: center;">
        <a href="/" class="btn btn-secondary">Acessar Painel</a>
        <button id="btn-copiar-resumo" class="btn btn-gold">Copiar Confirmação</button>
      </div>
    </div>
  `;

  content.querySelector('#btn-copiar-resumo').onclick = () => {
    const texto = `VEPC Gestão — Confirmação de Ativação:\nEmpresa: ${res.empresa_nome || convite.empresa.nome}\nUsuário: ${convite.usuario.email}\nAtivado em: ${res.ativada_em}\nAvaliação gratuita até: ${res.avaliacao_ate || res.teste_expira_em}`;
    navigator.clipboard?.writeText(texto);
    alert('Resumo copiado para a área de transferência!');
  };
}
